import React, { useEffect, useState } from 'react';
import { Box, Button } from '@cloudscape-design/components';
import useMidway from '../../hooks/useMidway';
import { getAuthUrl } from '../../contstant/apiEndpoints';
import AILoadingScreen from '../AILoadingScreen';

interface AuthHandlerProps {
  children: React.ReactNode;
}

export const AuthHandler: React.FC<AuthHandlerProps> = ({ children }) => {
  const { userAlias, isLoading: midwayLoading, isError: midwayError } = useMidway();
  const [authStatus, setAuthStatus] = useState<'checking' | 'ready' | 'establishing'>(
    'checking',
  );

  useEffect(() => {
    if (userAlias && !midwayLoading && !midwayError) {
      checkAndEstablishSessions();
    }
  }, [userAlias, midwayLoading, midwayError]);

  const checkAndEstablishSessions = async () => {
    setAuthStatus('establishing');

    try {
      // Quick check - if both services are accessible, we're done
      const [orbitOk, heliosOk] = await Promise.all([
        checkServiceAccess('orbit'),
        checkServiceAccess('helios'),
      ]);

      if (orbitOk && heliosOk) {
        setAuthStatus('ready');
        return;
      }

      // If not, establish sessions silently
      await establishMissingSessions(!orbitOk, !heliosOk);
      setAuthStatus('ready');
    } catch (error) {
      console.error('Auth setup failed:', error);
      // Fallback: redirect to establish first session
      window.location.href = getAuthUrl('orbit');
    }
  };

  const checkServiceAccess = async (service: 'orbit' | 'helios'): Promise<boolean> => {
    try {
      const url = getAuthUrl(service);

      const response = await fetch(url, {
        credentials: 'include',
        mode: 'cors',
        cache: 'no-cache',
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const establishMissingSessions = async (needsOrbit: boolean, needsHelios: boolean) => {
    const sessionPromises = [];

    if (needsOrbit) {
      sessionPromises.push(establishSession('orbit'));
    }
    if (needsHelios) {
      sessionPromises.push(establishSession('helios'));
    }

    await Promise.all(sessionPromises);
  };

  const establishSession = (service: 'orbit' | 'helios'): Promise<void> => {
    return new Promise((resolve, reject) => {
      const url = getAuthUrl(service);

      // Try iframe first (silent)
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;

      const cleanup = () => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error(`${service} session timeout`));
      }, 8000);

      iframe.onload = () => {
        clearTimeout(timeout);
        setTimeout(() => {
          cleanup();
          resolve();
        }, 1000);
      };

      iframe.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error(`${service} session failed`));
      };

      document.body.appendChild(iframe);
    });
  };

  // Loading state with AI-style loading screen
  if (midwayLoading || authStatus !== 'ready') {
    // Check if this is a returning user (has visited before)
    const isReturningUser = localStorage.getItem('gravion_visited') === 'true';

    // Mark as visited for future loads
    if (!isReturningUser) {
      localStorage.setItem('gravion_visited', 'true');
    }

    const firstVisitMessages = [
      'Initializing Gravion AI',
      'Calibrating audit intelligence',
      'Learning your preferences',
      'Preparing smart insights',
      'Ready to assist',
    ];

    const returningUserMessages = [
      'Reconnecting neural pathways',
      'Restoring your session',
      'Syncing latest updates',
      'Back online',
    ];

    const authMessages = ['Verifying identity', 'Securing connection', 'Authenticating'];

    const loadingMessages = midwayLoading
      ? authMessages
      : isReturningUser
        ? returningUserMessages
        : firstVisitMessages;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }}
      >
        <AILoadingScreen messages={loadingMessages} />
      </div>
    );
  }

  // Error state
  if (midwayError || !userAlias) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <Box fontSize="heading-s" color="text-status-error" margin={{ bottom: 'm' }}>
          Unable to sign in
        </Box>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};
